import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DataLookupService } from '@app/common/services/data-api/data-lookup.service';
import { PlexEnvironment } from '@app/env';
import { PxdGridViewEditService } from '@app/shared/kendoui/grid/services/pxd-grid-view-edit.service';
import { AreaTypes } from '@app/shared/models/area-types';
import { NameValueInt } from '@app/shared/models/name-value';
import { DialogService } from '@progress/kendo-angular-dialog';
import { AddEvent, DataStateChangeEvent, EditEvent, GridComponent, GridDataResult, RowArgs, SelectionEvent, } from '@progress/kendo-angular-grid';
import { SortDescriptor, State } from '@progress/kendo-data-query';
import { Asset, FormationTop, Table } from 'app/shared/models';
import { empty, from, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { GeneralAssetService } from './../../../assets/services/general-asset.service';

@Injectable({
  providedIn: 'root'
})
export class FormationTopGridService extends PxdGridViewEditService {
  private fb = inject(UntypedFormBuilder);
  private lookup = inject(DataLookupService);
  private generalAssetService = inject(GeneralAssetService);
  private dialogService = inject(DialogService);
  public environment = inject(PlexEnvironment);

  public currentAssetTeamId: number;
  private expandFilter: string = '&$expand=Area($select=AreaId,Description),TargetFormation($expand=TargetFormationCommonName($select=TargetFormationCommonNameId,Abbreviation))';
  private editedRowIndex: number;
  private newInQueue = false;
  public numericFilterFormat = '####';
  public editMode: boolean = false;
  public scenarioId: number;
  public viewData: Observable<GridDataResult>;
  public gridState: State = { sort: [], skip: 0, take: 50, filter: { filters: [], logic: 'and' } };
  public areas: NameValueInt[] = [];
  public targetFormations: NameValueInt[] = [];
  public areaMap = {};
  public targetFormationMap = {};
  public formGroup: UntypedFormGroup;
  public editDataItem: any;
  public new: boolean = false;
  public selected: RowArgs[] = [];
  public selectedKeys: string[] = [];
  public grid: GridComponent;
  public targetFormationCommonNamesList: any[] = [];
  public targetFormationCommonNamesMap = {};

  public get isInEditingMode(): boolean {
    return this.editedRowIndex !== undefined || this.new;
  }

  constructor() {
    super('FormationTops', ['FormationTopId']);
    this.createFormGroup = this.createFormGroup.bind(this);
  }

  public loadAssets() {
    this.loadLookUpData();

    this.viewData = this;
    this.read(this.gridState, this.formatQueryString());
  }

  public loadLookUpData() {
    let sort: SortDescriptor[] = [{ field: 'Description', dir: 'asc' }];
    let tfSort: SortDescriptor[] = [{ field: 'Abbreviation', dir: 'asc' }];

    let areaState: State = { sort: sort, filter: { filters: [{ field: 'AreaTypeId', operator: 'eq', value: AreaTypes.Development_Area }], logic: 'and' } };
    let tfState: State = { sort: tfSort };

    this.lookup.getNameValue(Table.Areas, areaState, null, true).then((x) => {
      this.areas = x || [];
      this.areaMap = new Map();
      this.areas.map((f) => this.areaMap[f.value] = f.name);
    });

    this.generalAssetService.getTargetFormationWithAbbreviation()
      .then(data => {
        this.targetFormationCommonNamesList = data.filter(x => x.TargetFormationCommonName != null).map(x => {
          return { TargetFormationId: x.TargetFormationId, Abbreviation: x.TargetFormationCommonName.Abbreviation };
        });
        this.targetFormationCommonNamesMap = new Map();
        this.targetFormationCommonNamesList.map(x => {
          this.targetFormationCommonNamesMap[x.TargetFormationId] = x.Abbreviation;
        });
      });
  }

  private formatQueryString(): string {
    return this.expandFilter;
  }

  private validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  public dataStateChange(state: DataStateChangeEvent) {
    this.gridState = state;
    this.read(this.gridState, this.formatQueryString());
  }

  public createFormGroup(args: any): any {
    const item = args.isNew ? new Asset() : args.dataItem;
    this.formGroup = this.fb.group({
      'FormationTopId': [item.FormationTopId || 0],
      'AreaId': [item.AreaId],
      'KBTVD': [item.KBTVD],
      'TargetFormationId': [item.TargetFormationId],
      'TreatingPressure': [item.TreatingPressure]
    });

    this.validateAllFormFields(this.formGroup);

    return this.formGroup;
  }

  public addHandler({ sender }: AddEvent): void {
    this.new = true;
  }

  public closeNew() {
    this.new = false;
  }

  public newDelete(event) {
    this.closeEditor(this.grid);

    this.selected.forEach((item) => {
      this.remove(item.dataItem);
    });

    this.selected = [];
    this.selectedKeys = [];
  }

  public closeEditor(grid: GridComponent, rowIndex: number = this.editedRowIndex): void {
    if (this.formGroup) {
      let contextRowIndex = rowIndex - this.gridState.skip;
      if (this.new) {
        (grid.data as any).data.splice(0, 0, this.formGroup.getRawValue());
        this.newInQueue = true;
        this.new = false;
      } else {
        (grid.data as any).data[contextRowIndex] = this.formGroup.getRawValue();
      }
      this.formGroup.reset();
    }
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
  }

  public editHandler({ sender, rowIndex, dataItem }: EditEvent): void {
    if (this.formGroup && !this.formGroup.valid && this.formGroup.dirty) {
      return;
    }
    this.editDataItem = dataItem;
    this.saveRow();
    this.createFormGroup({ dataItem });
    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public editClick({ dataItem, rowIndex, column, isEdited }: any): void {
    if (this.formGroup) {
      this.saveClick();
    }
    if (!isEdited) {
      if (this.newInQueue) { this.newInQueue = false; return; }
      this.editHandler({
        dataItem: dataItem,
        rowIndex: rowIndex,
        sender: this.grid,
        isNew: false
      });
    }
  }

  public selectionChange(event: SelectionEvent) {
    event.selectedRows.forEach((row) => {
      this.selected.push(row);
    });

    event.deselectedRows.forEach((row) => {
      this.selected = this.selected.filter(f => f.index !== row.index);
    });
  }

  public saveChanges(): Observable<any> {
    let saveChanges$ = from(this.canSave()).pipe(mergeMap(isValid => {
      if (isValid) {
        return super.saveChanges();
      }

      return empty();
    }));

    saveChanges$.subscribe();

    return saveChanges$;
  }

  private canSave(): Promise<boolean> {
    return new Promise((resolve, reject) => {

      //validation for null values
      let invalidUpdatedItems = this.updatedItems.filter(f => f.AreaId == null && f.TargetFormationId == null);
      let invalidCreatedItems = this.createdItems.filter(f => f.AreaId == null && f.TargetFormationId == null);

      if (invalidUpdatedItems.length || invalidCreatedItems.length) {
        this.showInvalidAreaFormationComboDialog('empty');
        resolve(false);
      }

      let promises: Array<Promise<any>> = [];

      //validation for invalid combos
      for (let item of this.updatedItems) {
        promises.push(this.isValidAreaFormationCombination(item));
      }

      for (let item of this.createdItems) {
        promises.push(this.isValidAreaFormationCombination(item));
      }

      return Promise.all(promises).then(items => {
        let invalidResults = items.filter(f => f === false);

        if (invalidResults.length) {
          this.showInvalidAreaFormationComboDialog('duplicate');
          return resolve(false);
        }

        //validation for local duplicates that don't yet exist in db
        let uniqueUpdates = this.updatedItems.reduce((arr, item) => {
          let exists = arr.find(x => x.AreaId == item.AreaId && x.TargetFormationId == item.TargetFormationId);
          if (!exists) {
            arr.push(item);
          }
          return arr;
        }, []);

        if (uniqueUpdates.length !== this.updatedItems.length) {
          this.showInvalidAreaFormationComboDialog('duplicate');
          return resolve(false);
        }

        return resolve(true);
      });
    });
  }

  public saveClick(): void {
    if (this.formGroup && !this.formGroup.valid) {
      return;
    }

    this.saveRow();
  }

  public saveRow(): void {
    if (this.isInEditingMode) {
      if (this.formGroup.dirty) {
        this.update(this.formGroup.value);
      }
    }

    this.closeEditor(this.grid);
  }

  private showInvalidAreaFormationComboDialog(type?: 'duplicate' | 'empty') {
    let content: string;

    switch (type) {
      case 'duplicate':
        content = 'The Area/Target Formation combination in one or more of the updated records is already being used. Please select another combination of values.';
        break;

      case 'empty':
        content = 'Both Area and Target Formation values cannot be empty. Please select a valid combination of values. ';
        break;

      default:
        content = 'Invalid Area/Target Formation combination. Please select another combination of values';
        break;
    }

    this.dialogService.open({
      title: 'Invalid Combination',
      content: content,
      actions: [
        { text: 'OK' }
      ],
      width: 450,
      minWidth: 250
    });
  }

  private async isValidAreaFormationCombination(formationTop: FormationTop) {
    let queryString = `${this.environment.apiUrl}formationtopsvalidation/IsValidAreaFormationCombination`;
    return this.http.post(queryString, formationTop, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }).toPromise();
  }
}